import { useState, useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';
import { findPolicyText, getSportRateBands, formatHourLabel, getMapEmbedSrc, getMapLinkUrl, buildDefaultFaqs, describeBandDays } from '../utils/bookingUtils';
import { APP_VERSION_NO, NAV_LINKS } from '../constants/bookingConst';
import { Menu, X, ChevronDown, Mail, Phone as PhoneIcon, MapPin } from 'lucide-react';
import TrackBookingModal from './TrackBookingModal';
import MapEmbed from './MapEmbed';
import BrandLogo from './BrandLogo';
import OpenPlayModal from './OpenPlayModal';
import '../css/LandingPage.css';
import { formatMoney } from '../utils/currencyUtil';

/** True when a rate band only applies to some weekdays (empty/absent = all). */
const isDayScoped = (band) => Array.isArray(band?.days) && band.days.length > 0 && band.days.length < 7;

// lucide-react has no brand icons, so this small inline SVG stands in for
// Facebook in the header/footer social links (same approach as App.jsx).
const FacebookIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 5.02 3.657 9.184 8.438 9.94v-7.03H7.898v-2.91h2.54V9.845c0-2.507 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562v1.877h2.773l-.443 2.91h-2.33V22c4.78-.756 8.437-4.92 8.437-9.94Z" />
  </svg>
);

// A tiny generic placeholder used wherever a photo is expected but neither
// admin-uploaded landing images nor court gallery photos exist yet.
const ImagePlaceholder = ({ className = '' }) => (
  <div className={`lp-img-placeholder ${className}`}>
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="M21 16l-5.5-5.5a1.5 1.5 0 00-2.12 0L3 21" />
    </svg>
  </div>
);

/**
 * Public marketing landing page (pickleball-only). Sourced from admin config
 * wherever a matching field exists; anything the config doesn't cover yet is
 * written as generic, non-fabricated copy — see MISSING_LANDING_PAGE_CONFIG
 * in the project root for a list of suggested fields to close that gap.
 */
function LandingPage({ onBookNow, onBookSport, scrollToId = null, onScrolledToSection }) {
  // landingConfig, not config: it is filled from the visitor's cache or the
  // build-time snapshot while the live config is still loading (App Check +
  // sign-in), and becomes the live config once that lands.
  const { landingConfig: config } = useConfig();
  const hasLandingConfig = !!config;
  const [navOpen, setNavOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  // "Track Booking" opens a modal here too (see TrackBookingModal) instead
  // of navigating to the standalone /bookings page — same as the guest-flow
  // header in App.jsx.
  const [showTrackBookingModal, setShowTrackBookingModal] = useState(false);
  // "Join Open Play" — opens OpenPlayModal preselected to whichever sport
  // card the guest clicked (see the Open Play section below). Sports with
  // openPlayEnabled off simply don't render a card, so a single-sport club
  // that hasn't turned this on never sees the feature at all.
  const [showOpenPlayModal, setShowOpenPlayModal] = useState(false);
  const [openPlaySportId, setOpenPlaySportId] = useState(null);

  // The guest-flow header (App.jsx) sends people "back" here via
  // scrollToId when they tap a nav link (Rates/Facilities/etc.) from a
  // booking screen. A direct link/reload (e.g. from the standalone
  // /bookings route) instead lands with a URL hash, so fall back to that.
  // Deferred until there is a config to render, since the target section
  // doesn't exist in the DOM until config-driven content has rendered.
  useEffect(() => {
    const targetId = scrollToId || (typeof window !== 'undefined' && window.location.hash ? window.location.hash.slice(1) : null);
    if (!targetId || !hasLandingConfig) return;
    const timer = setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
      if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
      onScrolledToSection?.();
    }, 60);
    return () => clearTimeout(timer);
  }, [scrollToId, hasLandingConfig]);

  const courtName = config?.courtName || 'Your Pickleball Court';
  const year = new Date().getFullYear();

  const heroImages = (config?.landingImages?.length ? config.landingImages : config?.galleryPhotos) || [];
  const mainImage = heroImages[0];
  const sideImages = heroImages.slice(1, 4);

  // Config -> Sports & Courts drives every sport-aware section below
  // (hero heading, rate cards, add-ons, FAQ copy) — matches Den's site,
  // where pickleball and tennis each get their own courts/rates/add-ons but
  // share one landing page. A single-sport club (the common case) still
  // sees the exact same layout as before, just sourced from config.sports[0]
  // instead of the old flat fields.
  const sportsList = config?.sports?.length ? config.sports : [{ id: 'default', name: 'Pickleball', icon: '🏓' }];
  const multiSport = sportsList.length > 1;
  const sportsNameList = sportsList.map(s => s.name).join(' & ');
  const courtsBySport = (sportId) => (config?.courts || []).filter(c => c.sportId === sportId);
  const totalCourts = config?.courts?.length || config?.numberOfCourts || 0;
  // "Two sports, one club" / "Three sports, one club" eyebrow for the "Pick
  // your court" section below — falls back to a plain numeral past 5 sports.
  const SPORT_COUNT_WORDS = { 2: 'Two', 3: 'Three', 4: 'Four', 5: 'Five' };
  const sportsCountLabel = SPORT_COUNT_WORDS[sportsList.length] || sportsList.length;


  // Admin-authored cards from Config -> Court Details, in their configured
  // order, plus the court count and cancellation policy which are derived
  // from elsewhere in config rather than typed here. Cards with nothing in
  // them are dropped so a half-finished edit never reaches guests.
  const courtDetails = (config?.courtDetails || []).filter(d => (d.title || '').trim() || (d.description || '').trim());
  // The "Hours" lines in the location block and footer used to read a
  // dedicated config.operatingHours field. Now that hours are just another
  // admin-authored card, this picks out whichever card is about hours by
  // title. Deliberately a loose match so "Hours", "Operating Hours" and
  // "Opening hours" all work; if the admin renames or deletes it, those two
  // lines simply don't render — exactly what used to happen when the old
  // field was left blank.
  const hoursDetail = courtDetails.find(d => /hour/i.test(d.title || ''));

  // ---- Hero / headline (Config -> Landing Page -> Headline Section) ----
  // Blank means "keep the generated copy", so each is checked at the
  // render site rather than defaulted here.
  const heroTitle = (config?.heroTitle || '').trim();
  const heroDescription = (config?.heroDescription || '').trim();
  const heroTags = (config?.heroTags || []).map(t => (t || '').trim()).filter(Boolean);

  // ---- About section (Config -> Landing Page -> About this club) ----
  // Each field falls back to the copy this section generated before these
  // were configurable, so an untouched deployment reads exactly as before.
  const aboutEyebrow = (config?.aboutEyebrow || '').trim() || `Welcome to ${courtName}`;
  const aboutTitle = (config?.aboutTitle || '').trim()
    || `A home for ${sportsNameList.toLowerCase()}${config?.locationText ? ` near ${config.locationText.split(',')[0]}` : ''}`;
  // Blank lines separate paragraphs, so the admin can tell a multi-part
  // story in one textarea without needing rich text.
  const aboutParagraphs = (config?.aboutStory || '')
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean);
  // Dedicated About photos (Config -> Landing Page), stacked beside the
  // story. Falls back to the hero collage's second image, as before.
  const aboutImages = config?.aboutImages || [];
  const features = [
    ...courtDetails.map(d => ({ title: d.title, desc: d.description })),
    totalCourts > 0 && { title: `${totalCourts} Court${totalCourts > 1 ? 's' : ''}`, desc: 'Book online in minutes.' },
    findPolicyText(config, /cancel/i) && { title: 'Cancellations', desc: findPolicyText(config, /cancel/i) },
  ].filter(Boolean);

  // One equipment/add-ons list per sport (Config -> Equipment tags each
  // item with a sportId) — matches Den's separate "Pickleball add-ons" /
  // "Tennis add-ons" tables. A sport with no equipment configured just
  // renders no table.
  const addOnsBySport = (sportId) => (config?.equipment || [])
    .filter(item => item.sportId === sportId)
    .map(item => ({ label: `${item.label} rental`, price: item.price, unit: `/${item.unit}` }));

  // Config -> Sports & Courts — sports with Open Play turned on (off by
  // default; an admin enables it per sport). Drives the dynamic cards in
  // the Open Play section below and pre-selects OpenPlayModal.
  const openPlaySports = (config?.sports || []).filter(s => s.openPlayEnabled);
  const openJoinModal = (sportId) => {
    setOpenPlaySportId(sportId);
    setShowOpenPlayModal(true);
  };

  // ---- Services section (Config -> Landing Page -> Services) ----
  // Pills under "What we offer". Stored as an array; blank entries are
  // dropped so a trailing comma in the admin's field can't render an
  // empty pill.
  const services = (config?.serviceTags || []).map(s => (s || '').trim()).filter(Boolean);
  // Title/description cards below the pills — same shape as courtDetails.
  const serviceCards = (config?.serviceCards || [])
    .filter(c => (c.title || '').trim() || (c.description || '').trim());

  // Admin-authored FAQs (Config -> Landing Page -> FAQs) replace the
  // generated set entirely. Left empty — the default — the generated list
  // is used, so its answers keep tracking config (rates, court counts,
  // cancellation policy) automatically. Entries with neither a question
  // nor an answer are dropped so a half-finished row never ships.
  const configuredFaqs = (config?.faqs || [])
    .filter(f => (f.question || '').trim() || (f.answer || '').trim())
    .map(f => ({ q: f.question, a: f.answer }));
  const faqs = configuredFaqs.length > 0
    ? configuredFaqs
    : buildDefaultFaqs(config).map(f => ({ q: f.question, a: f.answer }));

  const scrollTo = (id) => {
    setNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Both derived from the same admin field but in different forms — see
  // getMapEmbedSrc / getMapLinkUrl in bookingUtils.js for why an embed URL
  // must never be used as a link, and vice versa.
  const mapSrc = getMapEmbedSrc(config);
  const mapLinkUrl = getMapLinkUrl(config);

  return (
    <div className="landing-page-jassen">
      {/* ==================== HEADER ==================== */}
      <header className="lp-header">
        <div className="lp-header-inner">
          <div className="lp-brand">
            <BrandLogo src={config?.logoUrl} alt={courtName} className="lp-logo" />
            <span className="lp-brand-name">{courtName}</span>
          </div>

          <nav className="lp-nav">
            {NAV_LINKS.map(link => (
              <button key={link.id} type="button" className="lp-nav-link" onClick={() => scrollTo(link.id)}>
                {link.label}
              </button>
            ))}
            <button type="button" className="lp-nav-link" onClick={() => setShowTrackBookingModal(true)}>
              Track Booking
            </button>
          </nav>

          <button type="button" className="lp-book-btn" onClick={onBookNow}>Book a Court</button>
          <button type="button" className="lp-nav-toggle" onClick={() => setNavOpen(v => !v)} aria-label="Toggle menu">
            {navOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {navOpen && (
          <nav className="lp-nav-mobile">
            {NAV_LINKS.map(link => (
              <button key={link.id} type="button" className="lp-nav-link" onClick={() => scrollTo(link.id)}>
                {link.label}
              </button>
            ))}
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => { setShowTrackBookingModal(true); setNavOpen(false); }}
            >
              Track Booking
            </button>
            <button type="button" className="lp-book-btn" onClick={onBookNow}>Book a Court</button>
          </nav>
        )}
      </header>

      <TrackBookingModal isOpen={showTrackBookingModal} onClose={() => setShowTrackBookingModal(false)} />

      {/* ==================== HERO ==================== */}
      <section id="home" className="lp-hero">
        <div className="lp-hero-grid">
          <div className="lp-hero-copy">
            {config?.locationText && <p className="lp-eyebrow">{config.locationText}</p>}
            {/* An admin-written title renders verbatim — the generated one
                accents the sport names, which can't be inferred from
                free text. */}
            <h1 className="lp-h1">
              {heroTitle || <>Play <span className="lp-accent">{sportsNameList}</span> at {courtName}</>}
            </h1>
            {/* Falls back to a court count derived from config — the old
                copy spliced in courtSurface/operatingHours/whatsIncluded,
                which are admin-authored cards now (see `features`). */}
            <p className="lp-hero-desc">
              {heroDescription || (
                <>
                  {totalCourts ? `${totalCourts} ${multiSport ? '' : `${sportsList[0]?.name.toLowerCase()} `}court${totalCourts > 1 ? 's' : ''}` : `${sportsNameList} courts`}
                  {' '}&mdash; book online in minutes.
                </>
              )}
            </p>

            <div className="lp-hero-ctas">
              {multiSport ? (
                // One direct button per sport instead of a "Book a Court"
                // button that opens SportPickerModal — there's room for it
                // here (unlike the compact header nav, which still uses the
                // modal), so skip straight to that sport's booking flow.
                sportsList.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    // Each hero sport button gets its own colour from a
                    // fixed palette (see .lp-btn-sport-N in LandingPage.css)
                    // so the sports read as distinct choices rather than one
                    // primary CTA plus greyed-out siblings. Wraps around
                    // past the end of the palette for clubs with more
                    // sports than colours.
                    className={`lp-btn-sport lp-btn-sport-${i % 4}`}
                    onClick={() => onBookSport(s.id)}
                  >
                    Book {s.name.toLowerCase()}
                  </button>
                ))
              ) : (
                <button type="button" className="lp-btn-sport lp-btn-sport-0" onClick={onBookNow}>
                  Book {sportsList[0]?.name.toLowerCase() || 'pickleball'}
                </button>
              )}
              <button type="button" className="lp-btn-secondary" onClick={() => scrollTo('rates')}>View rates</button>
            </div>

            {/* Admin-listed pills replace the generated set entirely —
                mixing the two would double up the court counts. */}
            <div className="lp-pills">
              {heroTags.length > 0 ? (
                heroTags.map(tag => <span key={tag} className="lp-pill">{tag}</span>)
              ) : (
                <>
                  {multiSport
                    ? sportsList.map(s => (
                        courtsBySport(s.id).length > 0 && (
                          <span key={s.id} className="lp-pill">{s.icon ? `${s.icon} ` : ''}{courtsBySport(s.id).length} {s.name} court{courtsBySport(s.id).length > 1 ? 's' : ''}</span>
                        )
                      ))
                    : (totalCourts > 0 && <span className="lp-pill">{totalCourts} {sportsList[0]?.name} court{totalCourts > 1 ? 's' : ''}</span>)}
                  <span className="lp-pill">Open play</span>
                  <span className="lp-pill">Beginner friendly</span>
                </>
              )}
            </div>
          </div>

          <div className="lp-hero-media">
            {mainImage ? (
              <div className={`booking-gallery${sideImages.length === 0 ? ' single' : ''}`}>
                <img className="bg-main" src={mainImage.url} alt={courtName} />
                {sideImages.length > 0 && (
                  <div className="bg-side" style={{ '--gallery-side-count': sideImages.length }}>
                    {sideImages.map((photo, i) => (
                      <img key={photo.id || i} src={photo.url} alt={`${courtName} ${i + 2}`} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <ImagePlaceholder className="lp-hero-placeholder" />
            )}
          </div>
        </div>
      </section>

      {/* ==================== PICK YOUR COURT ==================== */}
      {multiSport && (
        <section id="pick-your-court" className="lp-pick-court">
          <p className="lp-eyebrow lp-eyebrow-center">{sportsCountLabel} sports, one club</p>
          <h2 className="lp-h2 lp-h2-center">Pick your court</h2>
          <div className="lp-pick-court-grid">
            {sportsList.map(sport => {
              const sportCourtCount = courtsBySport(sport.id).length;
              const fallbackDesc = sportCourtCount
                ? `${sportCourtCount} ${sport.name.toLowerCase()} court${sportCourtCount > 1 ? 's' : ''} available for booking online.`
                : `Book a ${sport.name.toLowerCase()} court online in minutes.`;
              return (
                <div className="lp-pick-court-card" key={sport.id}>
                  {sport.landingImageUrl ? (
                    <img className="lp-pick-court-img" src={sport.landingImageUrl} alt={sport.name} />
                  ) : (
                    <ImagePlaceholder className="lp-pick-court-img" />
                  )}
                  <div className="lp-pick-court-body">
                    <h3>{sport.icon ? `${sport.icon} ` : ''}{sport.name}</h3>
                    <p>{sport.landingDescription || fallbackDesc}</p>
                    <button type="button" className="lp-pick-court-link" onClick={() => onBookSport(sport.id)}>
                      Book a {sport.name.toLowerCase()} court &rarr;
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ==================== ABOUT ==================== */}
      <section id="about" className="lp-about">
        <div className="lp-section-grid">
          <div>
            {/* Eyebrow / heading / story are admin-authored (Config ->
                Landing Page -> About this club). Each falls back to the
                copy this section generated before those fields existed, so
                a deployment that never fills them in is unchanged. */}
            <p className="lp-eyebrow">{aboutEyebrow}</p>
            <h2 className="lp-h2">{aboutTitle}</h2>
            {aboutParagraphs.length > 0 ? (
              aboutParagraphs.map((para, i) => <p className="lp-body" key={i}>{para}</p>)
            ) : (
              <p className="lp-body">
                {courtName} offers {totalCourts ? `${totalCourts} standard-sized` : 'standard-sized'} {sportsNameList.toLowerCase()} courts.
              </p>
            )}
            {aboutParagraphs.length === 0 && (
              <p className="lp-body">
                Whether you're booking a court with friends, joining an open play session, or picking up a paddle for the very
                first time, there's a place for you here.
              </p>
            )}
          </div>
          <div className="lp-about-media">
            {aboutImages.length > 0
              ? aboutImages.map((photo, i) => (
                  <img key={photo.id || photo.url} src={photo.url} alt={`${courtName} court ${i + 1}`} />
                ))
              : heroImages[1]
                ? <img src={heroImages[1].url} alt={`${courtName} court`} />
                : <ImagePlaceholder />}
          </div>
        </div>
      </section>

      {/* ==================== FEATURES ==================== */}
      {features.length > 0 && (
        <section id="facilities" className="lp-features">
          <p className="lp-eyebrow lp-eyebrow-center">Court Features</p>
          <h2 className="lp-h2 lp-h2-center">Built for real play</h2>
          <div className="lp-feature-grid">
            {features.map((f, i) => (
              <div key={i} className="lp-feature-card">
                <h3>{f.title}</h3>
                {/* .admin-text (global.css) — these are the Court Details
                    cards, typed in a textarea, so honour the admin's line
                    breaks rather than collapsing them into one paragraph. */}
                <p className="admin-text">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== RATES ==================== */}
      <section id="rates" className="lp-rates">
        <p className="lp-eyebrow lp-eyebrow-light">Rates & Booking</p>
        <h2 className="lp-h2 lp-h2-light">Court rates</h2>
        <p className="lp-body lp-body-light">
          Reserve online, upload your payment receipt, and we confirm your slot shortly after.
        </p>

        {/* One rate-card pair + add-ons table per sport (matches Den's
            separate "Pickleball rates" / "Tennis rates" layout). A
            single-sport club just gets one block, visually identical to
            the old fixed pickleball-only section. */}
        {sportsList.map(sport => {
          // One card per configured rate band instead of a fixed day/night
          // pair, so a club charging three or more rates shows all of them.
          // getSportRateBands falls back to the legacy two-rate fields, so a
          // config saved before bands existed renders exactly as it did.
          const sportBands = getSportRateBands(config, sport.id);
          const sportAddOns = addOnsBySport(sport.id);
          return (
            <div className="lp-rates-sport-block" key={sport.id}>
              {multiSport && (
                <h3 className="lp-rates-sport-heading">{sport.icon ? `${sport.icon} ` : ''}{sport.name}</h3>
              )}
              <div className="lp-rates-grid">
                {sportBands.map((band) => (
                  <div className="lp-rate-card" key={band.id}>
                    <span className="lp-rate-label">
                      {band.name ? `${band.name} · ` : ''}
                      {formatHourLabel(Number(band.startHour) || 0)}–{formatHourLabel(Number(band.endHour) || 0)}
                      {/* Only shown when the band is day-scoped — otherwise a
                          weekend rate and a weekday rate covering the same
                          hours read as two identical cards with different
                          prices. */}
                      {isDayScoped(band) ? ` · ${describeBandDays(band)}` : ''}
                    </span>
                    <span className="lp-rate-value">{formatMoney(band.price, config)}<small>/hour</small></span>
                  </div>
                ))}
              </div>

              {sportAddOns.length > 0 && (
                <div className="lp-addons">
                  <span className="lp-eyebrow lp-eyebrow-light">{sport.name} Add-ons</span>
                  {sportAddOns.map((a, i) => (
                    <div key={i} className="lp-addon-row">
                      <span>{a.label}</span>
                      <span>{formatMoney(a.price, config)}{a.unit}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* ==================== SERVICES ==================== */}
      <section id="services" className="lp-services">
        <p className="lp-eyebrow">Services</p>
        <h2 className="lp-h2">What we offer</h2>
        {services.length > 0 && (
          <div className="lp-service-pills">
            {services.map(s => <span key={s} className="lp-pill lp-pill-outline">{s}</span>)}
          </div>
        )}
        {/* Admin-authored cards (Config -> Landing Page -> Services), plus
            the contact card, which is generated from the club's phone
            number rather than typed out. */}
        <div className="lp-highlight-grid">
          {serviceCards.map(card => (
            <div className="lp-highlight-card" key={card.id}>
              <h3>{card.title}</h3>
              <p className="admin-text">{card.description}</p>
            </div>
          ))}
          {config?.contactNumber && (
            <div className="lp-highlight-card">
              <h3>Questions?</h3>
              <p>Call or message us at {config.contactNumber} and we'll be happy to help.</p>
            </div>
          )}
        </div>
      </section>

      {/* ==================== OPEN PLAY ==================== */}
      <section id="open-play" className="lp-openplay">
        <p className="lp-eyebrow lp-eyebrow-light">Open Play</p>
        <h2 className="lp-h2 lp-h2-light">Join an open play session</h2>
        <p className="lp-body lp-body-light">
          Pay per player, meet other players, and jump straight into friendly games — no need to book a whole court.
        </p>

        {openPlaySports.length > 0 && (
          <div className="lp-openplay-sports">
            {openPlaySports.map(sport => (
              <div className="lp-openplay-sport-card" key={sport.id}>
                <span className="lp-openplay-sport-icon">{sport.icon || '🎾'}</span>
                <h3>{sport.name}</h3>
                {sport.openPlayNote && <p className="lp-openplay-sport-note">{sport.openPlayNote}</p>}
                <p className="lp-openplay-sport-price">{formatMoney(Number(sport.openPlayPricePerPlayer || 0), config)} / player</p>
                <button type="button" className="lp-btn-primary" onClick={() => openJoinModal(sport.id)}>
                  Join Open Play
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="lp-openplay-box">
          <p><strong>Fair queueing.</strong> Players rotate on and off court in the order they arrive.</p>
          <p><strong>Respect your fellow players</strong> at all times, and keep the game fun.</p>
          <p><strong>Ask our staff</strong> for the current open play schedule when you arrive.</p>
        </div>
      </section>

      <OpenPlayModal
        isOpen={showOpenPlayModal}
        onClose={() => setShowOpenPlayModal(false)}
        config={config}
        sports={openPlaySports}
        initialSportId={openPlaySportId}
      />

      {/* ==================== LOCATION ==================== */}
      <section id="location" className="lp-location">
        <div className="lp-section-grid">
          <div>
            <p className="lp-eyebrow">Location</p>
            <h2 className="lp-h2">Visit us{config?.locationText ? ` at ${courtName}` : ''}</h2>
            {config?.locationText && <p className="lp-body">{config.locationText}</p>}
            <div className="lp-location-meta">
              {hoursDetail?.description && <span><strong>Hours:</strong> {hoursDetail.description}</span>}
              {config?.contactNumber && <span><strong>Contact:</strong> {config.contactNumber}</span>}
            </div>
            {mapLinkUrl && (
              <a href={mapLinkUrl} target="_blank" rel="noopener noreferrer" className="lp-btn-primary lp-btn-block">
                Get Directions
              </a>
            )}
          </div>
          <div className="lp-map">
            {mapSrc ? (
              /* Deferred until the section nears the viewport — the embed
                 pulls ~40 of Google's own requests behind it, and mounting
                 that with the rest of the page is what made this screen slow
                 to become usable. See MapEmbed.jsx. */
              <MapEmbed src={mapSrc} />
            ) : (
              <div className="lp-map-placeholder"><MapPin size={32} /><span>Add a location to show a map here.</span></div>
            )}
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section id="faq" className="lp-faq">
        <p className="lp-eyebrow">FAQ</p>
        <h2 className="lp-h2">Frequently asked questions</h2>
        <div className="lp-faq-list">
          {faqs.map((item, i) => (
            <div key={i} className={`lp-faq-item ${openFaq === i ? 'open' : ''}`}>
              <button type="button" className="lp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span>{item.q}</span>
                <ChevronDown size={18} className="lp-faq-chevron" />
              </button>
              {openFaq === i && <p className="lp-faq-a">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ==================== CTA + FOOTER ==================== */}
      <section className="lp-cta">
        <h2 className="lp-h2 lp-h2-light lp-h2-center">Ready to play?</h2>
        <p className="lp-body lp-body-light lp-body-center">
          Book your court today and enjoy pickleball at {courtName}.
        </p>
        <div className="lp-cta-actions">
          <button type="button" className="lp-btn-primary" onClick={onBookNow}>Book pickleball</button>
        </div>

        <div className="lp-footer-grid">
          {config?.locationText && (
            <div>
              <span className="lp-eyebrow lp-eyebrow-light">Address</span>
              <p>{config.locationText}</p>
            </div>
          )}
          {config?.contactNumber && (
            <div>
              <span className="lp-eyebrow lp-eyebrow-light">Contact</span>
              <p>{config.contactNumber}</p>
              {config?.emailAddress && <p>{config.emailAddress}</p>}
            </div>
          )}
          {hoursDetail?.description && (
            <div>
              <span className="lp-eyebrow lp-eyebrow-light">Hours</span>
              <p>{hoursDetail.description}</p>
              <div className="lp-social">
                {config?.facebookPage && (
                  <a href={`https://facebook.com/${config.facebookPage}`} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <FacebookIcon />
                  </a>
                )}
                {config?.emailAddress && (
                  <a href={`mailto:${config.emailAddress}`} aria-label="Email"><Mail size={16} /></a>
                )}
                {config?.contactNumber && (
                  <a href={`tel:${config.contactNumber}`} aria-label="Phone"><PhoneIcon size={16} /></a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Version shares the copyright line but sits hard right — it is a
            support detail ("what does the footer say?"), not a second
            statement about the club, so it stays out of the centred text.
            Hidden entirely when the build has no REACT_APP_VERSION_NO set. */}
        <p className="lp-copyright">
          <span className="lp-copyright-text">© {year} {courtName}. All rights reserved.</span>
          {APP_VERSION_NO && <span className="lp-version">{APP_VERSION_NO}</span>}
        </p>
      </section>
    </div>
  );
}

export default LandingPage;
